import { memo, useCallback, useMemo } from 'react';
import { ManualArea, StationSpec } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@/modules/common/components/inputs';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { isManualArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { NumUtils } from '@/modules/common/utils';

const LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS: {
  label: string;
  value: StationSpec['loadHandlingDurationStrategyInSimulation'];
}[] = [
  { label: 'By height', value: 'BY_HEIGHT' },
  { label: 'By override', value: 'BY_OVERRIDE' },
];

const getSelectedManualArea = (
  selectedAreas: ReturnType<typeof useReadSelectedAreas>,
): ManualArea | null => {
  const areas = areasToFlatCollection(selectedAreas);

  if (areas.length !== 1) return null;

  const area = areas[0];

  if (!isManualArea(area)) return null;

  return area;
};

const canEditLoadDuration = (loadHandlingType: ManualArea['loadHandlingType']) =>
  loadHandlingType === 'INTAKE' || loadHandlingType === 'INTAKE_AND_DELIVERY';

const canEditUnloadDuration = (loadHandlingType: ManualArea['loadHandlingType']) =>
  loadHandlingType === 'DELIVERY' || loadHandlingType === 'INTAKE_AND_DELIVERY';

function ManualStationSpecPropertiesComponent() {
  const selectedAreas = useReadSelectedAreas();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const area = useMemo(() => getSelectedManualArea(selectedAreas), [selectedAreas]);

  const onZChange = useCallback(
    (newValueMm: number) => {
      if (!area || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('manualArea.stationSpec.z', newValue);

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          z: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onStrategyChange = useCallback(
    (newValue: StationSpec['loadHandlingDurationStrategyInSimulation']) => {
      if (!area) return;

      fddmSvc.setPreference(
        'manualArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        newValue,
      );

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          loadHandlingDurationStrategyInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onLoadDurationChange = useCallback(
    (newValue: number) => {
      if (!area || Number.isNaN(newValue)) return;

      fddmSvc.setPreference('manualArea.stationSpec.loadDurationOverrideInSimulation', newValue);

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          loadDurationOverrideInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  const onUnloadDurationChange = useCallback(
    (newValue: number) => {
      if (!area || Number.isNaN(newValue)) return;

      fddmSvc.setPreference('manualArea.stationSpec.unloadDurationOverrideInSimulation', newValue);

      fddmSvc.updateArea({
        ...area,
        stationSpec: {
          ...area.stationSpec,
          unloadDurationOverrideInSimulation: newValue,
        },
      });
    },
    [area, fddmSvc],
  );

  if (!area) return null;

  const { stationSpec, loadHandlingType } = area;
  const showOverrideFields = stationSpec.loadHandlingDurationStrategyInSimulation === 'BY_OVERRIDE';

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.load_elevation'>
        <NumberInput
          value={NumUtils.metersToMmRounded(stationSpec.z)}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onZChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.load_duration_strategy'>
        <SelectInput
          options={LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS}
          onChange={onStrategyChange}
          value={stationSpec.loadHandlingDurationStrategyInSimulation}
        />
      </PropertiesLabel>

      {showOverrideFields && canEditLoadDuration(loadHandlingType) && (
        <PropertiesLabel i18nkey='interface:properties.area.load_duration'>
          <NumberInput
            value={stationSpec.loadDurationOverrideInSimulation ?? 0}
            unit='sec'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onLoadDurationChange}
          />
        </PropertiesLabel>
      )}

      {showOverrideFields && canEditUnloadDuration(loadHandlingType) && (
        <PropertiesLabel i18nkey='interface:properties.area.unload_duration'>
          <NumberInput
            value={stationSpec.unloadDurationOverrideInSimulation ?? 0}
            unit='sec'
            prefix={<GapHorizontalIcon />}
            sx={{ width: '100%' }}
            minValue={0}
            onChange={onUnloadDurationChange}
          />
        </PropertiesLabel>
      )}
    </>
  );
}

export const StationSpecProperties = memo(ManualStationSpecPropertiesComponent);
